import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';
import './FlightChargesManagement.css';

const FlightChargesManagement = () => {
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [selectedFlight, setSelectedFlight] = useState('');
  const [applyToAll, setApplyToAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [charges, setCharges] = useState({
    commission_fee: 0,
    cancellation_fee: 250,
    change_fee_tier1: 50,
    change_fee_tier2: 150,
    change_fee_tier3: 300,
    change_fee_tier4: 500
  });

  // Tooltip Component
  const Tooltip = ({ text, id }) => (
    <div
      className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#333] text-white text-[11px] rounded shadow-lg whitespace-nowrap z-[60] transition-opacity duration-200 ${activeTooltip === id ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
    >
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-[#333]"></div>
    </div>
  );

  useEffect(() => {
    fetchFlights();
  }, []);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching flights from API...');

      // Note: api interceptor returns response.data directly
      const data = await api.get('/flight-management/charges');

      console.log('✅ API Response received:', data);
      console.log('✈️ Flights array:', data.data);
      console.log('📈 Flights count:', data.data?.length);

      if (data.success) {
        const flightsData = data.data || [];
        console.log('💾 Setting flights state with:', flightsData);
        setFlights(flightsData);
      } else {
        console.error('❌ API returned success: false');
        toast.error('Failed to load flights');
      }
    } catch (error) {
      console.error('❌ Error fetching flights:', error);
      toast.error(error.message || 'Failed to fetch flights');
    } finally {
      setLoading(false);
      console.log('✔️ Loading complete');
    }
  };

  const handleChargeChange = (field, value) => {
    // Validate numeric input
    const numValue = parseFloat(value);
    if (value === '' || (!isNaN(numValue) && numValue >= 0)) {
      setCharges(prev => ({
        ...prev,
        [field]: value === '' ? '' : numValue
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!applyToAll && !selectedFlight) {
      toast.error('Please select a flight or check "Apply to All Routes"');
      return;
    }

    if (charges.commission_fee === '' || charges.cancellation_fee === '' ||
      charges.change_fee_tier1 === '' || charges.change_fee_tier2 === '' ||
      charges.change_fee_tier3 === '' || charges.change_fee_tier4 === '') {
      toast.error('All charge fields are required');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        flight_id: applyToAll ? null : parseInt(selectedFlight),
        commission_fee: parseFloat(charges.commission_fee),
        cancellation_fee: parseFloat(charges.cancellation_fee),
        change_fee_tier1: parseFloat(charges.change_fee_tier1),
        change_fee_tier2: parseFloat(charges.change_fee_tier2),
        change_fee_tier3: parseFloat(charges.change_fee_tier3),
        change_fee_tier4: parseFloat(charges.change_fee_tier4),
        apply_to_all: applyToAll
      };

      console.log('🚀 Submitting charges update:', payload);

      const response = await api.post('/flight-management/charges', payload);

      console.log('✅ Update response:', response);

      // Since api interceptor returns response.data directly, check response.success
      if (response.success) {
        toast.success(response.message || 'Flight charges updated successfully');

        console.log('🔄 Refreshing flights list...');
        // Refresh flights list
        await fetchFlights();

        // Reset form if not applying to all
        if (!applyToAll) {
          setSelectedFlight('');
        }

        console.log('✅ Update completed successfully');
      } else {
        toast.error(response.message || 'Failed to update flight charges');
      }
    } catch (error) {
      console.error('❌ Error updating charges:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to update flight charges');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyToAllChange = (checked) => {
    setApplyToAll(checked);
    if (checked) {
      setSelectedFlight('');
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="flight-charges-page">
      <div className="charges-header">
        <h2>Flight Charges</h2>
        <button onClick={handleBack} className="btn-back">
          <i className="fa fa-arrow-left"></i> Back
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading flights...</p>
        </div>
      ) : (
        <div className="charges-form-wrapper">

          <form onSubmit={handleSubmit} className="charges-form">
            <div className="form-row-inline">
              {/* Flight Selection */}
              {!applyToAll && (
                <div className="form-group">
                  <label htmlFor="flight_route">
                    Select Flight <span className="required">*</span>
                    <div className="relative inline-block ml-1">
                      <i
                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                        onMouseEnter={() => setActiveTooltip('flight')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      <Tooltip text="Select a specific flight to update its charges" id="flight" />
                    </div>
                  </label>
                  <div className="searchable-select-wrapper">
                    <SearchableSelect
                      options={flights.map(flight => ({
                        value: flight.id,
                        label: `${flight.registration_number} (${flight.flight_number})`
                      }))}
                      value={selectedFlight}
                      onChange={(e) => {
                        const flightId = e.target.value;
                        console.log('✈️ Flight selected:', flightId);
                        setSelectedFlight(flightId);

                        // Find selected flight and populate charges
                        const selectedFlightData = flights.find(f => String(f.id) === String(flightId));
                        if (selectedFlightData) {
                          console.log('📊 Populating charges:', selectedFlightData);
                          setCharges({
                            commission_fee: selectedFlightData.commission_fee || 0,
                            cancellation_fee: selectedFlightData.cancellation_fee || 250,
                            change_fee_tier1: selectedFlightData.change_fee_tier1 ?? 50,
                            change_fee_tier2: selectedFlightData.change_fee_tier2 ?? 150,
                            change_fee_tier3: selectedFlightData.change_fee_tier3 ?? 300,
                            change_fee_tier4: selectedFlightData.change_fee_tier4 ?? 500
                          });
                        }
                      }}
                      placeholder="Select flight"
                      name="flight_route"
                    />
                  </div>
                </div>
              )}

              {/* Agency Fee (Commission) */}
              <div className="form-group">
                <label htmlFor="commission_fee">
                  Agency Fee ( % ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('commission')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Percentage commission charged on each booking" id="commission" />
                  </div>
                </label>
                <input
                  type="number"
                  id="commission_fee"
                  className="form-control"
                  placeholder="5"
                  value={charges.commission_fee}
                  onChange={(e) => handleChargeChange('commission_fee', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Cancellation Fee */}
              <div className="form-group">
                <label htmlFor="cancellation_fee">
                  Cancellation Fee ( $ ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('cancellation')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Fee charged when a booking is cancelled" id="cancellation" />
                  </div>
                </label>
                <input
                  type="number"
                  id="cancellation_fee"
                  className="form-control"
                  placeholder="250"
                  value={charges.cancellation_fee}
                  onChange={(e) => handleChargeChange('cancellation_fee', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Flight Change Fee — 4 Tiers */}
              <div className="form-group">
                <label htmlFor="change_fee_tier1">
                  Change Fee &gt;72hr ( $ ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('tier1')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Change fee when more than 72 hours before departure" id="tier1" />
                  </div>
                </label>
                <input
                  type="number"
                  id="change_fee_tier1"
                  className="form-control"
                  placeholder="50"
                  value={charges.change_fee_tier1}
                  onChange={(e) => handleChargeChange('change_fee_tier1', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="change_fee_tier2">
                  Change Fee 24-72hr ( $ ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('tier2')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Change fee when 24 to 72 hours before departure" id="tier2" />
                  </div>
                </label>
                <input
                  type="number"
                  id="change_fee_tier2"
                  className="form-control"
                  placeholder="150"
                  value={charges.change_fee_tier2}
                  onChange={(e) => handleChargeChange('change_fee_tier2', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="change_fee_tier3">
                  Change Fee 6-24hr ( $ ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('tier3')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Change fee when 6 to 24 hours before departure" id="tier3" />
                  </div>
                </label>
                <input
                  type="number"
                  id="change_fee_tier3"
                  className="form-control"
                  placeholder="300"
                  value={charges.change_fee_tier3}
                  onChange={(e) => handleChargeChange('change_fee_tier3', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="change_fee_tier4">
                  Change Fee 1-6hr ( $ ) <span className="required">*</span>
                  <div className="relative inline-block ml-1">
                    <i
                      className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                      onMouseEnter={() => setActiveTooltip('tier4')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip text="Change fee when 1 to 6 hours before departure" id="tier4" />
                  </div>
                </label>
                <input
                  type="number"
                  id="change_fee_tier4"
                  className="form-control"
                  placeholder="500"
                  value={charges.change_fee_tier4}
                  onChange={(e) => handleChargeChange('change_fee_tier4', e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            {/* Apply to All Checkbox */}
            <div className="checkbox-row">
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    id="all_routes_checkbox"
                    checked={applyToAll}
                    onChange={(e) => handleApplyToAllChange(e.target.checked)}
                  />
                  <span className="checkbox-text">Apply To All Routes</span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-small"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FlightChargesManagement;
